import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";

import moment from "moment";

import FaiqIntegration from "../Columns/FaiqIntegration";

import MDTypography from "components/Basics/MDTypography";
import TypeCell from "./TypeCell";
import StatusCell from "./StatusCell";
import { TableRow } from "./TableParts";

// Column widths
export const COLUMNS = {
	checkbox: 40,
	name: "1fr",
	type: 80,
	status: 90,
	FAIQ: 135,
	access: 70,
	updatedAt: 120,
	actions: 40
};

/**
 * Single row component
 */
export const AssetRow = React.memo(
	({
		asset,
		selected,
		onSelect,
		onClick,
		onMenuClick,
		onAccessClick,
		t,
		style,
		canChangeStatus = true
	}) => {
		const handleCheckboxClick = useCallback(
			e => {
				e.stopPropagation();
				onSelect(asset);
			},
			[asset, onSelect]
		);

		const handleMenuClick = useCallback(
			e => {
				e.stopPropagation();
				onMenuClick(e, asset);
			},
			[asset, onMenuClick]
		);

		const handleAccessClick = useCallback(
			e => {
				e.stopPropagation();
				onAccessClick(asset);
			},
			[asset, onAccessClick]
		);

		const handleRowClick = useCallback(() => {
			onClick(asset);
		}, [asset, onClick]);

		const formattedDate = asset.updatedAt
			? moment(asset.updatedAt).format("DD/MM/YYYY HH:mm")
			: "-";

		return (
			<TableRow style={style} selected={selected} onClick={handleRowClick}>
				{/* Actions */}
				<Box
					sx={{ width: COLUMNS.actions, flexShrink: 0, display: "flex", justifyContent: "center" }}
				>
					<IconButton size="small" onClick={handleMenuClick} sx={{ p: 0.5 }}>
						<MoreVertIcon fontSize="small" />
					</IconButton>
				</Box>

				{/* Checkbox */}
				<Box sx={{ width: COLUMNS.checkbox, flexShrink: 0 }}>
					<Checkbox checked={selected} onClick={handleCheckboxClick} size="small" sx={{ p: 0.5 }} />
				</Box>

				{/* Name with icon */}
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						gap: 1,
						minWidth: 0,
						pr: 1.5
					}}
				>
					<Tooltip title={asset.name} placement="top-start">
						<MDTypography
							fontWeight="bold"
							variant="button"
							color="info"
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%"
							}}
						>
							{asset.name}
						</MDTypography>
					</Tooltip>
				</Box>

				{/* Type */}
				<Box sx={{ width: COLUMNS.type, flexShrink: 0, display: "flex", justifyContent: "center" }}>
					<TypeCell asset={asset} />
				</Box>

				{/* Status */}
				<Box
					sx={{ width: COLUMNS.status, flexShrink: 0, display: "flex", justifyContent: "center" }}
				>
					<StatusCell value={asset} t={t} disabled={!canChangeStatus} />
				</Box>

				{/* FAIQ */}
				<Box
					sx={{
						pl: 1.5,
						pr: 0.5,
						width: COLUMNS.FAIQ,
						flexShrink: 0,
						display: "flex",
						justifyContent: "center"
					}}
				>
					<FaiqIntegration value={asset} />
				</Box>

				{/* Access */}
				<Box
					sx={{ width: COLUMNS.access, flexShrink: 0, display: "flex", justifyContent: "center" }}
				>
					<Tooltip
						title={t(`ASSETS.ACCESS.${asset.access}`, { defaultValue: asset.access })}
						placement="top"
					>
						<Box
							onClick={handleAccessClick}
							sx={{
								display: "flex",
								alignItems: "center",
								cursor: "pointer",
								"&:hover": {
									opacity: 0.7
								}
							}}
						>
							{asset.access === "public" ? (
								<LockOpenIcon sx={{ fontSize: 16 }} color="success" />
							) : (
								<LockIcon sx={{ fontSize: 16 }} color="secondary" />
							)}
						</Box>
					</Tooltip>
				</Box>

				{/* Updated At */}
				<Box
					sx={{
						width: COLUMNS.updatedAt,
						flexShrink: 0,
						display: "flex",
						justifyContent: "center"
					}}
				>
					<Typography variant="caption" color="text.secondary">
						{formattedDate}
					</Typography>
				</Box>
			</TableRow>
		);
	}
);
